// src/hooks/useTokenMonitor.js
// Monitors JWT token expiration and provides warnings before logout
// Reads session timing from localStorage (non-sensitive timestamps only — JWT stays in httpOnly cookie)

import { useState, useEffect, useCallback } from 'react';
import { isDev } from '../constants/config';

/**
 * Hook to monitor JWT token expiration
 * @param {Object} options
 * @param {number} options.warningThreshold - Percentage of time remaining to show warning (default: 0.10 = 10%)
 * @param {function} options.onExpired - Callback when token expires
 * @param {function} options.onWarning - Callback when warning threshold reached
 * @param {boolean} options.isAuthenticated - Whether user is currently authenticated
 */
export function useTokenMonitor({
  warningThreshold = 0.10,
  onExpired,
  onWarning,
  isAuthenticated
}) {
  const [tokenStatus, setTokenStatus] = useState({
    isExpired: false,
    isWarning: false,
    remainingTime: null, // in seconds
    expiresAt: null,
    totalLifetime: null // in seconds
  });

  const checkToken = useCallback(() => {
    const expiresAt = parseInt(localStorage.getItem('session_expires_at'), 10);
    // Use the stored token lifetime (TOKEN_EXPIRY_SECONDS from server) — always fixed.
    // Do NOT compute expiresAt - issuedAt: issuedAt is not updated on silent refresh,
    // so totalLifetime would grow unboundedly, making warningPercentage always look large.
    const tokenLifetime = parseInt(localStorage.getItem('session_token_lifetime'), 10);

    if (!expiresAt || !tokenLifetime) {
      setTokenStatus(prev => ({ ...prev, isExpired: true, remainingTime: 0 }));
      return;
    }

    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    const totalLifetime = tokenLifetime;
    const remainingTime = expiresAt - now;
    const remainingPercentage = remainingTime / totalLifetime;

    // DEBUG: Log token status (only in development)
    if (isDev) {
      console.log('[TokenMonitor] Check:', {
        totalLifetime: totalLifetime + 's',
        remainingTime: remainingTime + 's',
        remainingPercentage: (remainingPercentage * 100).toFixed(1) + '%',
        warningThreshold: (warningThreshold * 100) + '%',
        isWarning: remainingPercentage <= warningThreshold
      });
    }

    // Token has expired
    if (remainingTime <= 0) {
      setTokenStatus({
        isExpired: true,
        isWarning: false,
        remainingTime: 0,
        expiresAt: new Date(expiresAt * 1000),
        totalLifetime
      });
      return { expired: true, warning: false, remainingTime: 0 };
    }

    // Token is in warning zone (less than threshold remaining)
    const isWarning = remainingPercentage <= warningThreshold;

    setTokenStatus({
      isExpired: false,
      isWarning,
      remainingTime,
      expiresAt: new Date(expiresAt * 1000),
      totalLifetime
    });

    return { expired: false, warning: isWarning, remainingTime };
  }, [warningThreshold]);

  // Main monitoring effect
  useEffect(() => {
    if (!isAuthenticated) {
      if (isDev) console.log('[TokenMonitor] Not authenticated, skipping monitor');
      return;
    }

    if (isDev) console.log('[TokenMonitor] Starting monitor...');
    let warningTriggered = false;
    let expiredTriggered = false;
    let lastExpiresAt = null; // track token changes to reset flags on silent refresh

    const runCheck = () => {
      // Detect when a new token was issued (silentRefresh) and reset flags
      const currentExpiresAt = parseInt(localStorage.getItem('session_expires_at'), 10);
      if (currentExpiresAt && currentExpiresAt !== lastExpiresAt) {
        if (lastExpiresAt !== null) {
          // A new token was issued — reset so warning/expired fire again at the right time
          warningTriggered = false;
          expiredTriggered = false;
        }
        lastExpiresAt = currentExpiresAt;
      }

      const status = checkToken();

      if (!status) return;

      // Trigger warning callback once
      if (status.warning && !warningTriggered && !status.expired) {
        warningTriggered = true;
        if (onWarning) {
          onWarning(status.remainingTime);
        }
      }

      // Trigger expired callback once
      if (status.expired && !expiredTriggered) {
        expiredTriggered = true;
        if (onExpired) {
          onExpired();
        }
      }
    };

    // Adaptive interval: frequent checks near expiry, relaxed when fresh
    let timeoutId;

    const getInterval = () => {
      const expiresAt = parseInt(localStorage.getItem('session_expires_at'), 10);
      const tokenLifetime = parseInt(localStorage.getItem('session_token_lifetime'), 10);
      if (!expiresAt || !tokenLifetime) return 30000;
      const pct = (expiresAt - Math.floor(Date.now() / 1000)) / tokenLifetime;
      if (pct > 0.5) return 60000;   // >50% remaining: every 60s
      if (pct > 0.10) return 15000;  // 10-50%: every 15s
      return 5000;                    // <10%: every 5s
    };

    const scheduleNextCheck = () => {
      timeoutId = setTimeout(() => {
        runCheck();
        scheduleNextCheck();
      }, getInterval());
    };

    // Initial check
    runCheck();
    scheduleNextCheck();

    // Also check when tab becomes visible (user returns to app)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        clearTimeout(timeoutId);
        runCheck();
        scheduleNextCheck();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also check when network comes back online
    const handleOnline = () => {
      clearTimeout(timeoutId);
      runCheck();
      scheduleNextCheck();
    };
    window.addEventListener('online', handleOnline);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [isAuthenticated, checkToken, onExpired, onWarning]);

  // Format remaining time for display
  const formatRemainingTime = useCallback(() => {
    const seconds = tokenStatus.remainingTime;
    if (!seconds || seconds <= 0) return 'Expirée';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    }
    return `${minutes} min`;
  }, [tokenStatus.remainingTime]);

  return {
    ...tokenStatus,
    formatRemainingTime,
    checkToken
  };
}

export default useTokenMonitor;
